import { useMemo } from "react";

import { max, mean, min, orderBy } from "lodash";
import moment from "moment";

import { useUser } from "@components/user-context";
import { type User } from "@internal/user";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  todayLocalAsISODate,
} from "@north-beam/nb-common";
import {
  type MetricPoint,
  addToMetricPoint,
  newMetricPoint,
} from "@north-beam/nb-common/metrics";
import { parseDateRangeArgument } from "@utils/index";

import {
  type Adjustment,
  type AdoptableMetrics,
  type AdoptedMetrics,
  type AdoptedMetricsData,
  type BaselineFilters,
  type BaselineMetrics,
  type DaySelection,
  type ExclusionReason,
  type KpiMetricsData,
  type MetricPointWithDate,
  type WizardStep,
} from "../types";
import {
  adjustMetricsGrossRevRow,
  getCompletedWizardSteps,
  percentile,
  sumCogs,
} from "../utils";

interface Args {
  baselineMetrics: BaselineMetrics;
  baselineDays: MetricPointWithDate[];
  baselineFilters: BaselineFilters;
  wizardStep: WizardStep;
  kpiMetricsData: KpiMetricsData[];
  adoptableKpiMetrics: AdoptableMetrics;
  adoptedMetrics: AdoptedMetrics[];
  adoptedKpiData: AdoptedMetricsData[];
  buildKpiMetrics: (
    attributionModel: string,
    attributionWindow: IMCAttributionWindowDays,
    accountingMode: INBAccountingMode,
  ) => SalesPageMetric[];
}

export const useDaysSelection = ({
  baselineMetrics,
  baselineDays,
  baselineFilters,
  wizardStep,
  kpiMetricsData,
  adoptableKpiMetrics,
  adoptedMetrics,
  adoptedKpiData,
  buildKpiMetrics,
}: Args) => {
  const { user } = useUser();

  return useMemo(() => {
    return getDaysSelection({
      baselineMetrics,
      baselineDays,
      baselineFilters,
      wizardStep,
      kpiMetricsData,
      adoptableKpiMetrics,
      adoptedMetrics,
      adoptedKpiData,
      user,
      buildKpiMetrics,
    });
  }, [
    user,
    baselineMetrics,
    baselineDays,
    baselineFilters,
    wizardStep,
    kpiMetricsData,
    adoptableKpiMetrics,
    adoptedMetrics,
    adoptedKpiData,
    buildKpiMetrics,
  ]);
};

export const getDaysSelection = ({
  baselineMetrics,
  baselineDays,
  baselineFilters,
  wizardStep,
  kpiMetricsData,
  adoptableKpiMetrics,
  adoptedMetrics,
  adoptedKpiData,
  user,
  buildKpiMetrics,
}: Args & { user: User }) => {
  const result: DaySelection[] = [];

  const completedSteps = getCompletedWizardSteps(wizardStep);

  let profitabilityRev = baselineMetrics.rev;
  let profitabilityTxns = baselineMetrics.txns;

  if (baselineFilters.profitableDayThreshold.method === "first-time") {
    profitabilityRev = baselineMetrics.ftRev;
    profitabilityTxns = baselineMetrics.ftTxns;
  }

  if (baselineFilters.profitableDayThreshold.method === "returning") {
    profitabilityRev = baselineMetrics.rtnRev;
    profitabilityTxns = baselineMetrics.rtnTxns;
  }

  const isDiscountsThresholdBlended =
    baselineFilters.discountsThreshold.method === "blended";
  const discountsRevMetric = isDiscountsThresholdBlended
    ? baselineMetrics.rev
    : baselineMetrics.ftRev;
  const orderDiscountsMetric = isDiscountsThresholdBlended
    ? baselineMetrics.orderDiscounts
    : baselineMetrics.ftOrderDiscounts;

  const cogs = baselineFilters.cogs;

  const bottomRevPercentile = percentile(
    baselineDays.map((e) => baselineMetrics.rev.calculate(e)),
    baselineFilters.lowestRevenueThreshod.value,
  );
  const topRevPercentile = percentile(
    baselineDays.map((e) => baselineMetrics.rev.calculate(e)),
    100 - baselineFilters.highestRevenueThreshod.value,
  );
  const bottomSpendPercentile = percentile(
    baselineDays.map((e) => baselineMetrics.spend.calculate(e)),
    baselineFilters.lowestSpendThreshod.value,
  );
  const topSpendPercentile = percentile(
    baselineDays.map((e) => baselineMetrics.spend.calculate(e)),
    100 - baselineFilters.highestSpendThreshod.value,
  );
  const bottomTxnsPercentile = percentile(
    baselineDays.map((e) => baselineMetrics.txns.calculate(e)),
    baselineFilters.lowestOrdersThreshod.value,
  );
  const topTxnsPercentile = percentile(
    baselineDays.map((e) => baselineMetrics.txns.calculate(e)),
    100 - baselineFilters.highestOrdersThreshod.value,
  );
  const cogsTotal = sumCogs(cogs);
  const excludedRanges = baselineFilters.excludedDateRanges.map((e) => {
    const range = parseDateRangeArgument(e, todayLocalAsISODate());
    return {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate),
    };
  });

  const kpiExcludedDays: string[] = [];

  for (const kpiFilter of baselineFilters.kpiFilters) {
    const metrics = buildKpiMetrics(
      kpiFilter.attributionModel,
      kpiFilter.attributionWindow,
      kpiFilter.accountingMode,
    );
    const metric = metrics.find((e) => e.id === kpiFilter.metricId);
    const data = kpiMetricsData.find((e) => e.kpiMetricId === kpiFilter.id);
    const rows = data?.data?.rows;

    if (!metric || !rows || !data || data.status === "loading") {
      continue;
    }

    const metricPoints = rows[0] ? rows[0].metrics : [];

    for (const metricPoint of metricPoints) {
      if (
        kpiFilter.equation === "gt" &&
        metric.calculate(metricPoint) > kpiFilter.value
      ) {
        kpiExcludedDays.push(metricPoint.date);
      }

      if (
        kpiFilter.equation === "lt" &&
        metric.calculate(metricPoint) < kpiFilter.value
      ) {
        kpiExcludedDays.push(metricPoint.date);
      }
    }
  }

  for (const row of baselineDays) {
    const day = moment(row.date);

    const rowGross = adjustMetricsGrossRevRow(row, user.ecomAccountingSettings);
    const spendTotal = baselineMetrics.spend.calculate(rowGross);
    const ordersTotal = profitabilityTxns.calculate(rowGross);
    const rowGrossRev = profitabilityRev.calculate(rowGross);
    const rowNetRev =
      rowGrossRev -
      ordersTotal *
        (cogs.shipping.value +
          cogs.taxes.value +
          cogs.discounts.value +
          cogs.returns.value);
    const marketingContribution =
      spendTotal + cogs.otherMarketingCosts.value * ordersTotal;
    const marketingContributionMargin = marketingContribution / rowNetRev;

    let isExcluded = false;
    let willBeExcluded = false;
    const exclusionReason: ExclusionReason[] = [];

    let isUnprofitable = false;

    if (
      baselineFilters.profitableDayThreshold.equation ===
      "contribution-margin-is-at-least"
    ) {
      isUnprofitable =
        marketingContributionMargin <
        baselineFilters.profitableDayThreshold.value / 100;
    } else {
      isUnprofitable =
        profitabilityRev.calculate(rowGross) - cogsTotal * ordersTotal <
        baselineFilters.profitableDayThreshold.value;
    }

    if (baselineFilters.profitableDayThreshold.enabled && isUnprofitable) {
      if (completedSteps.includes("adjust-for-profitability")) {
        isExcluded = true;
        exclusionReason.push("unprofitable");
      }

      if (!isExcluded && completedSteps.includes("select-baseline-days")) {
        willBeExcluded = true;
        exclusionReason.push("unprofitable");
      }
    }

    const discountRevThresholdValue =
      discountsRevMetric.calculate(rowGross) *
      (baselineFilters.discountsThreshold.value / 100);
    const isAboveDiscountThreshold =
      orderDiscountsMetric.calculate(rowGross) > discountRevThresholdValue;

    if (
      baselineFilters.discountsThreshold.enabled &&
      isAboveDiscountThreshold
    ) {
      if (completedSteps.includes("adjust-for-promotions-and-outliers")) {
        isExcluded = true;
        exclusionReason.push("high-promotion");
      }

      if (!isExcluded && completedSteps.includes("adjust-for-profitability")) {
        willBeExcluded = true;
        exclusionReason.push("high-promotion");
      }
    }

    if (
      excludedRanges.some(
        (e) => day.isSameOrAfter(e.startDate) && day.isSameOrBefore(e.endDate),
      )
    ) {
      if (completedSteps.includes("adjust-for-promotions-and-outliers")) {
        isExcluded = true;
        exclusionReason.push("manually-excluded");
      }

      if (!isExcluded && completedSteps.includes("adjust-for-profitability")) {
        willBeExcluded = true;
        exclusionReason.push("manually-excluded");
      }
    }

    if (
      baselineFilters.lowestRevenueThreshod.enabled &&
      baselineMetrics.rev.calculate(row) < bottomRevPercentile
    ) {
      if (completedSteps.includes("adjust-for-promotions-and-outliers")) {
        isExcluded = true;
        exclusionReason.push("lowest-rev");
      }

      if (!isExcluded && completedSteps.includes("adjust-for-profitability")) {
        willBeExcluded = true;
        exclusionReason.push("lowest-rev");
      }
    }

    if (
      baselineFilters.highestRevenueThreshod.enabled &&
      baselineMetrics.rev.calculate(row) > topRevPercentile
    ) {
      if (completedSteps.includes("adjust-for-promotions-and-outliers")) {
        isExcluded = true;
        exclusionReason.push("highest-rev");
      }

      if (!isExcluded && completedSteps.includes("adjust-for-profitability")) {
        willBeExcluded = true;
        exclusionReason.push("highest-rev");
      }
    }

    if (
      baselineFilters.lowestOrdersThreshod.enabled &&
      baselineMetrics.txns.calculate(row) < bottomTxnsPercentile
    ) {
      if (completedSteps.includes("adjust-for-promotions-and-outliers")) {
        isExcluded = true;
        exclusionReason.push("least-txns");
      }

      if (!isExcluded && completedSteps.includes("adjust-for-profitability")) {
        willBeExcluded = true;
        exclusionReason.push("least-txns");
      }
    }

    if (
      baselineFilters.highestOrdersThreshod.enabled &&
      baselineMetrics.txns.calculate(row) > topTxnsPercentile
    ) {
      if (completedSteps.includes("adjust-for-promotions-and-outliers")) {
        isExcluded = true;
        exclusionReason.push("most-txns");
      }

      if (!isExcluded && completedSteps.includes("adjust-for-profitability")) {
        willBeExcluded = true;
        exclusionReason.push("most-txns");
      }
    }

    if (
      baselineFilters.lowestSpendThreshod.enabled &&
      baselineMetrics.spend.calculate(row) < bottomSpendPercentile
    ) {
      if (completedSteps.includes("adjust-for-promotions-and-outliers")) {
        isExcluded = true;
        exclusionReason.push("lowest-spend");
      }

      if (!isExcluded && completedSteps.includes("adjust-for-profitability")) {
        willBeExcluded = true;
        exclusionReason.push("lowest-spend");
      }
    }

    if (
      baselineFilters.highestSpendThreshod.enabled &&
      baselineMetrics.spend.calculate(row) > topSpendPercentile
    ) {
      if (completedSteps.includes("adjust-for-promotions-and-outliers")) {
        isExcluded = true;
        exclusionReason.push("highest-spend");
      }

      if (!isExcluded && completedSteps.includes("adjust-for-profitability")) {
        willBeExcluded = true;
        exclusionReason.push("highest-spend");
      }
    }

    if (kpiExcludedDays.includes(row.date)) {
      if (completedSteps.includes("adjust-for-incrementality")) {
        isExcluded = true;
        exclusionReason.push("incrementality-filters");
      }

      if (
        !isExcluded &&
        completedSteps.includes("adjust-for-promotions-and-outliers")
      ) {
        willBeExcluded = true;
        exclusionReason.push("incrementality-filters");
      }
    }

    result.push({
      day: row.date,
      rev: baselineMetrics.rev.calculate(row),
      revGross: baselineMetrics.rev.calculate(rowGross),
      spend: baselineMetrics.spend.calculate(row),
      txns: baselineMetrics.txns.calculate(row),
      discountValue: baselineMetrics.orderDiscounts.calculate(row),
      cogs: cogsTotal * baselineMetrics.txns.calculate(row),
      isExcluded,
      willBeExcluded,
      exclusionReason,
    });
  }

  const adjustmentExcludedDays: string[] = getAdjustmentExcludedDays(
    baselineFilters.adjustment,
    adoptableKpiMetrics,
    adoptedMetrics,
    adoptedKpiData,
    result,
  );

  return result.map((daySelection) => {
    if (adjustmentExcludedDays.includes(daySelection.day)) {
      return {
        ...daySelection,
        isExcluded: true,
        exclusionReason: [
          ...daySelection.exclusionReason,
          "adjusted",
        ] as ExclusionReason[],
      };
    } else {
      return daySelection;
    }
  });
};

const getAdjustmentExcludedDays = (
  adjustment: Adjustment | null,
  adoptableKpiMetrics: AdoptableMetrics,
  adoptedMetrics: AdoptedMetrics[],
  adoptedKpiData: AdoptedMetricsData[],
  daysSelection: DaySelection[],
) => {
  if (!adjustment) {
    return [];
  }

  const adoptedMetric = adoptedMetrics.find(
    (e) => e.metricId === adjustment.metricId,
  );
  const metric = adoptableKpiMetrics.metrics.find(
    (e) => e.id === adjustment.metricId,
  );
  const rows = adoptedKpiData.find((e) => e.metricId === adoptedMetric?.id)
    ?.data?.rows;

  if (!metric || !rows) {
    return [];
  }

  const excludedDays = daysSelection
    .filter((e) => e.isExcluded)
    .map((e) => e.day);

  const valuesByDay: Record<string, MetricPoint> = {};
  const dimensionValues: Record<string, { day: string; value: number }[]> = {};

  for (const row of rows) {
    const values = [];

    for (const metricPoint of row.metrics) {
      if (excludedDays.includes(metricPoint.date)) {
        continue;
      }

      if (!valuesByDay[metricPoint.date]) {
        valuesByDay[metricPoint.date] = newMetricPoint();
      }

      addToMetricPoint(valuesByDay[metricPoint.date], metricPoint);

      const metricValue = metric.calculate(metricPoint);

      values.push({
        day: metricPoint.date,
        value: isNaN(metricValue) || !isFinite(metricValue) ? 0 : metricValue,
      });
    }

    if (!values.map((e) => e.value).every((e) => e === 0)) {
      dimensionValues[row.dimensions["Platform (Northbeam)"]] = values;
    }
  }

  const topLevelValues = Object.entries(valuesByDay).map(([day, value]) => ({
    day,
    value: metric.calculate(value),
  }));

  dimensionValues["top"] = topLevelValues;

  const values = dimensionValues[adjustment.dimension];
  const avgVal = mean(values.map((e) => e.value));
  const minVal = min(values.map((e) => e.value))!;
  const maxVal = max(values.map((e) => e.value))!;
  const sortedValues = orderBy(
    values,
    ["value"],
    [adjustment.value > avgVal ? "asc" : "desc"],
  );

  let rejectedDays: string[] = [];

  for (let i = 0; i < sortedValues.length; i++) {
    const workingSet = [...sortedValues];
    const rejected = workingSet.splice(0, i);

    const adjustedValue = mean(workingSet.map((e) => e.value));
    const delta =
      adjustment.value > avgVal
        ? adjustment.value - adjustedValue
        : adjustedValue - adjustment.value;

    const workingSetNext = [...sortedValues];
    const rejectedNext = workingSetNext.splice(0, i + 1);

    const adjustedValueNext = mean(workingSetNext.map((e) => e.value));
    const deltaNext =
      adjustment.value > avgVal
        ? adjustment.value - adjustedValueNext
        : adjustedValueNext - adjustment.value;

    if (delta < 0 || deltaNext < 0) {
      if (
        Math.abs(adjustment.value - adjustedValueNext) <
        Math.abs(adjustment.value - adjustedValue)
      ) {
        rejectedDays = rejectedNext.map((e) => e.day);
      } else {
        rejectedDays = rejected.map((e) => e.day);
      }

      break;
    }
  }

  if (adjustment.value > maxVal || adjustment.value < minVal) {
    return sortedValues
      .map((e) => e.day)
      .filter((e) => e !== sortedValues[sortedValues.length - 1].day);
  } else {
    return rejectedDays;
  }
};
